import {
  Component, OnInit, ChangeDetectorRef, ViewChildren, QueryList,
  OnDestroy, Output, EventEmitter, ViewChild,
} from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { CustomValidators } from 'shared/validation/custom-validators';
import * as _ from 'lodash';

import { Observable ,  Subscription } from 'rxjs';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';

// data, models, helpers
import { segmentAnimation } from '../base/animations';
import { detailsSegmentData } from '../../segment-data/details-segment-data';
import * as utils from 'utils/utils';
import {
  mainFieldsGroup, coreFieldsGroup, partHistoryFieldsGroup, warrantyFieldsGroup, heavyDutyFieldsGroup,
} from './fields-info';
import { Detail } from '../../models/detail.model';
import * as dateUtils from 'utils/date';
import { createOption } from 'shared/form/select/option-helpers';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';

// components
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { DetailsControlComponent } from 'shared/details-page/details-control/details-control.component';
import { SimpleConfirmModalComponent } from 'shared/modal/simple-confirm-modal/simple-confirm-modal.component';
import { PCDBModalComponent } from '../pcdb-modal/pcdb-modal.component';

// services
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { LookupsService } from 'shared/lookups/lookups.service';
import { ProductGroupsService } from 'shared/product-groups/product-groups.service';
import { CoreService } from 'shared/global-core/core.service';
import { BrandService } from 'shared/brand/brand.service';
import {mergeMap} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'details-segment',
  templateUrl: 'details.component.html',
  styleUrls: ['../base/_base-segment.component.scss', 'details.component.scss'],
  animations: [segmentAnimation],
})
export class DetailsComponent extends BaseSegmentComponent implements OnInit, OnDestroy {

  @Output() forceSave: EventEmitter<any> = new EventEmitter();

  @ViewChildren(DetailsControlComponent)
  declare detailsControls: QueryList<DetailsControlComponent>;

  @ViewChild(PCDBModalComponent, {static: false})
  pcdbModal: PCDBModalComponent | void;

  @ViewChild('confirmPartTypeChangeModal', {static: false})
  confirmPartTypeChangeModal: SimpleConfirmModalComponent | void;

  declare segmentForm: UntypedFormGroup;

  segmentData: any = detailsSegmentData;
  segmentName: string = 'item-details'; // used in save request
  pathToScores: string = 'segments.details.tallies';
  detail: Detail;
  productGroupsData: Array<any>;

  // grouped data, used to render dom
  mainFields: Array<any> = [];
  coreInfoFields: Array<any> = [];
  partHistoryFields: Array<any> = [];
  warrantyFields: Array<any> = [];
  heavyDutyFields: Array<any> = [];

  // subscriptoins:
  loadCoreClassSub: Subscription;
  lookupsServiceSub: Subscription;
  productGroupsServiceSub: Subscription;

  typeaheadLoading: boolean;
  typeaheadNoResults: boolean;

  receivedGtin: string;

  private pcdbData: any = {
    selectedPartType: null,
    partTerminology: null,
  };

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get category() {
    return this.segmentForm.get('partCategory').value;
  }

  get subCategory() {
    return this.segmentForm.get('partSubcategory').value;
  }

  /*
    * replaced parent
   */
  get scorecardFields(): any {
    return _.get(this.scorecardData, `segments.details.fields`);
  }

  // returns segmentName where conditionals are placed
  // redefines parent's method
  get conditionalsSegmentName(): string {
    return 'details';
  }

  // returns prefix for each conditional rule
  // redefines parent's method
  get conditionalsFieldName(): string {
    return 'details';
  }

  /*******************************************
  * COMPONENT SETTERS
  *******************************************/

  set category(category: string) {
    this.segmentForm.get('partCategory').setValue(category);
  }

  set subCategory(subcategory: string) {
    this.segmentForm.get('partSubcategory').setValue(subcategory);
  }

  set partType(partType: string) {
    this.segmentForm.get('partType').setValue(partType);
  }

  set partTypeName(partTypeName: string) {
    this.segmentForm.get('partTypeName').setValue(partTypeName);
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    private lookupsService: LookupsService,
    private productGroupsService: ProductGroupsService,
    private coreService: CoreService,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.createForm();
    if (_.size(this.itemDetail)) {
      this.handleAllDataUpdate();
    }
    this.separateSegmentFields(this.segmentData);
    _.defer(() => {
      this.runAnimation('show');
    });
  }

  ngOnDestroy() {
    if (this.loadCoreClassSub) { this.loadCoreClassSub.unsubscribe(); }
    if (this.lookupsServiceSub) { this.lookupsServiceSub.unsubscribe(); }
    if (this.productGroupsServiceSub) { this.productGroupsServiceSub.unsubscribe(); }
    super.ngOnDestroy();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onChangeTypeaheadLoading(e: boolean): void {
    this.typeaheadLoading = e;
  }

  onChangeTypeaheadNoResults(e: boolean, field_map: string): void {
    this.typeaheadNoResults = e;
     this.segmentForm.get(field_map).setValue(null);
  }

  onSelectTypeahead(e: TypeaheadMatch, field_map: string): void {
     this.segmentForm.get(field_map).setValue(e.item.id);
  }

  onBlurTypeahead(e: any, hiddenField: string, displayField: string) {
    if (!this.segmentForm.get(hiddenField).value) {
      this.segmentForm.get(displayField).setValue('');
    }
  }

  // handle selects:
  onSelectChange(value: any, item: any) {
    switch (item.field_map) {
      case 'productGroupId':
        this.handleProductGroupsSelect(value.option);
        break;
      case 'coreId':
        this.setCoreGroup(value.option, item);
        break;
      default:
        break;
    }
    this.markAsDirty();
    super.onSelectChange(value, item);
  }

  // part type modal handlers
  onPartTypeClick() {
    let partTerminology = null;
    if (!this.bulkEdit) {
      const data = _.get(this.itemDetail, 'partTerminology');
      if (data && data.id) {
        partTerminology = {
          part_terminology_id: data.id,
          part_terminology_name: data.name,
          category_name: data.category.name,
          sub_category_name: data.subcategory.name,
        };
      }
    }
    this.pcdbData.partTerminology = partTerminology;
    if (this.pcdbModal instanceof PCDBModalComponent) {
      this.pcdbModal.show(partTerminology, this.bulkEdit);
    }
  }

  onSelectPartType(partType: any) {
    this.pcdbData.selectedPartType = partType;
    _.delay(() => {
      if (this.confirmPartTypeChangeModal instanceof SimpleConfirmModalComponent) {
        this.confirmPartTypeChangeModal.show();
      }
    }, 200);
  }

  onPartTypeChangeConfirm() {
    this.updatePartType(this.pcdbData.selectedPartType);
    this.pcdbData.selectedPartType = null;
    this.pcdbData.partTerminology = null;
  }

  onPartTypeChangeCancel() {
    if (this.pcdbModal instanceof PCDBModalComponent) {
      this.pcdbModal.show(this.pcdbData.partTerminology, this.bulkEdit);
    }
    this.pcdbData.selectedPartType = null;
    this.pcdbData.partTerminology = null;
  }

  onDateChange(selectedDate: string, item: any) {
    if (item.item_name === 'details.availableDate') {
      dateUtils.handleDateAheadChange(
        this.segmentForm.get('effectiveDate'),
        'details.effectiveDate',
        selectedDate,
        this.segmentData
      );
    }
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public updatePartType(partType: any) {
    this.partType = partType.part_terminology_id;
    this.partTypeName = partType.part_terminology_name;
    this.category = partType.category_name;
    this.subCategory = partType.sub_category_name;
    this.markAsDirty();
  }

  public setBulkPartType(data: any = {}) {
    if (!data.details) { return; }
    const { partType, partTypeName, partCategory, partSubcategory } = data.details;
    this.partType = partType;
    this.partTypeName = partTypeName;
    this.category = partCategory;
    this.subCategory = partSubcategory;
  }

  // replaced parent
  public getSegmentFormData(): any {
    return this.segmentForm.value;
  }

  public getSegmentStoredBulkData(): any {
    return this.getSegmentFormData();
  }

  createForm(): void {
    this.detail = new Detail();
    _.each(this.segmentData, (item: any) => {
      if (item.field_map) {
        const validators = this.getFieldValidators(item);

        _.set(this.detail, item.field_map, new UntypedFormControl(_.get(item, 'default_value', ''),  validators) );

        if (_.has(item, 'api_lookup.lookup')) {
          _.set(this.detail, item.api_lookup.field_map, new UntypedFormControl(_.get(item.api_lookup, 'default_value', '')) );
          item.dataSource = new Observable((observer: any) => {
              // Runs on every search
              observer.next(this.detail[item.api_lookup.field_map].value);
            })
            .pipe(mergeMap((token: string) => {
              return this.lookupsService.fetch(item.api_lookup.lookup, token);
            }));
        }
      }
    });
    this.segmentForm = this.fb.group(this.detail);
  }

  hasItemInScores(item: any): boolean {
    return _.has(this.segmentScores, item.item_name);
  }

  isItemInvalidInScores(item: any): boolean {
    if (!_.has(this.segmentScores, item.item_name)) { return false; }
    const itemSegment = _.get(this.segmentScores, item.item_name);
    return !itemSegment.valid;
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected updateControlsValidation(): void {
    _.each(this.segmentData, (item: any) => {
      const control: AbstractControl = this.segmentForm.get(item.field_map);
      if (control) {
        this.updateControlValidators(control, this.getFieldValidators(item));
      }
    });
  }

  protected handleScoreDetailsExpand(event: any): void {
    if (!this.editMode) { return; }
    const item = event.item;
    const control = this.findControlByFieldMap(item.field_map);
    if (control) { control.focusControl(item.field_map); }
  }

  // replace parent method:
  protected setBulkData(): void {
    this.setData({
      details: this.getBulkSegmentData(this.segmentName)
    });
  }

  /*
    * Updates form group after fetching item details
   */
  protected setData(itemDetail: any = {}): void {
    this.receivedGtin = _.get(itemDetail, 'details.gtin', '');
    _.each(this.segmentData, (item: any) => {
      const validators = this.getFieldValidators(item);
      if (item.field_map) {
        const control = this.segmentForm.get(item.field_map);
        const value = _.get(itemDetail, item.item_name, '');
        if (item.lookup) {
          control.setValue(value);
        } else {
          control.setValue(typeof value === 'string' ? value : String(value));
        }
        this.updateControlValidators(control, validators);
        this.ref.markForCheck();
        if (item.api_lookup) {
          if (item.api_lookup.type === 'typeahead') {
            this.segmentForm.controls[item.api_lookup.field_map].setValue(_.get(itemDetail, item.api_lookup.item_name, ''));
            this.ref.markForCheck();
          } else {
            this.lookupsServiceSub = this.lookupsService.fetch(item.api_lookup.lookup, _.get(itemDetail, item.api_lookup.field_map, ''))
              .subscribe((lookup) => {
                item.lookup = this.addLookupPlaceholder(lookup);
                this.ref.markForCheck();
              });
          }
        }
      }
    });
    this.loadProductGroups(_.get(itemDetail, 'details.group.id', null));
    this.loadCoreClass(_.get(itemDetail, 'details.core', null));
    // set part type from local storage in bulk edit mode
    if (this.bulkEdit) {
      this.setBulkPartType(itemDetail);
    }
  }

  protected getFieldValidators(item: SegmentFieldItem): Array<ValidatorFn> {
    if (item.field_map === 'gtin' && this.itemDetail) {
      // if validation error received from server
      if (_.get(this.itemDetail, 'validation.combinedErrors', []).indexOf('details.gtin') !== -1) {
        return [
          ...this.getCombinedErrorsValidators(),
          ...super.getFieldValidators(item),
        ];
      }
    }
    return super.getFieldValidators(item);
  }

  /*
    * Replaced parent
   */
  protected getScorecardField(name: string): any {
    return _.find(this.scorecardFields, ['path', name]);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  /*
    * Separate segment fields into groups
    * (main, core, part history, warranty, heavy duty, etc) and chunks
   */
  private separateSegmentFields(segmentData: Array<any>): void {
    this.mainFields = this.findSegmentGroupFields(segmentData, mainFieldsGroup);
    this.coreInfoFields = this.findSegmentGroupFields(segmentData, coreFieldsGroup);
    this.partHistoryFields =  this.findSegmentGroupFields(segmentData, partHistoryFieldsGroup);
    this.warrantyFields =  this.findSegmentGroupFields(segmentData, warrantyFieldsGroup);
    this.heavyDutyFields =  this.findSegmentGroupFields(segmentData, heavyDutyFieldsGroup);
  }

  /*
    * Finds fields in the segment data list and groups fields accoring on fieldsGroup array
   */
  private findSegmentGroupFields(segmentData: Array<any>, fieldsGroup: Array<any>) {
    return _.reduce(fieldsGroup, (result: Array<any>, group: Array<any>) => {
      const chunk = _.reduce(group, (fieldsList: Array<any>, fieldMap: any) => {
        if (_.isArray(fieldMap)) {
          const combinedItems: any[] = _.filter(segmentData, (segmentItem: any) => _.includes(fieldMap, segmentItem.field_map));
          fieldsList.push(combinedItems);
        } else {
          fieldsList.push(_.find(segmentData, ['field_map', fieldMap]));
        }
        return fieldsList;
      }, []);
      result.push(chunk);
      return result;
    }, []);
  }

  private loadProductGroups(productGroupId: number): void {
    this.productGroupsServiceSub = this.productGroupsService.fetch(this.brandService.getBrand())
    .subscribe((data: any) => {
      this.productGroupsData = data;
      const productGroup = utils.getFieldByName('details.group.id', this.segmentData);
      productGroup.lookup = this.getProductGroupLookup(data);
      this.setProductSubGroupLookup(data, productGroupId);
      this.ref.markForCheck();
    });
  }

  private getProductGroupLookup(data: any): any {
    return _.reduce(data, (memo: Array<any>, item: any) => {
      memo.push(createOption(item.id, `${item.name} (${item.code})`));
      return memo;
    }, [this.optionPlaceholder]);
  }

  private setProductSubGroupLookup(data: any, productGroupId: any): void {
    const productGroup: any = _.find(data, (item: any) => {
      if (typeof productGroupId === 'string') {
        productGroupId = parseInt(productGroupId);
      }
      return item.id === productGroupId;
    });
    const productSubGroupSegment = utils.getFieldByName('details.group.subGroup.id', this.segmentData);
    if (_.has(productGroup, 'product_sub_group')) {
      productSubGroupSegment.lookup = this.getProductGroupLookup(productGroup.product_sub_group);
    } else {
      productSubGroupSegment.lookup = [];
    }
    this.ref.markForCheck();
  }

  private handleProductGroupsSelect(value: number): void {
    this.setProductSubGroupLookup(this.productGroupsData, value);
    this.segmentForm.get('productSubGroupId').reset();
    this.ref.markForCheck();
  }

  private loadCoreClass(core: any): void {
    this.loadCoreClassSub = this.coreService.fetch(this.brandService.getBrand())
    .subscribe((data: any) => {
      const coreClass = utils.getFieldByName('details.core.id', this.segmentData);
      coreClass.lookup = this.getCoreClassLookup(data);
      this.ref.markForCheck();
      this.setCoreGroup(_.get(core, 'id', 0), coreClass);
    });
  }

  private setCoreGroup(value: string | number, item: any): void {
    value = _.isString(value) ? parseInt(value) : value;
    const coreClass = _.find(item.lookup, (coreItem: any) => coreItem.option === value);
    this.segmentForm.get('coreGroup').setValue(_.get(coreClass, 'group', ''));
    this.segmentForm.get('coreReturnDaysToExpiry').setValue(_.get(coreClass, 'days', ''));
    this.ref.markForCheck();
  }

  private getCoreClassLookup(data: any): any {
    return _.reduce(data, (memo: Array<any>, item: any) => {
      memo.push({
        option: item.id,
        title: item.class,
        group: item.group,
        days: item.days
      });
      return memo;
    }, [this.optionPlaceholder]);
  }

  public isClickable(item: SegmentFieldItem): boolean {
    return _.includes(['partType', 'partTypeName'], item.field_map) && this.editMode;
  }

  private getCombinedErrorsValidators(): Array<any> {
    const validators = [];
    validators.push(CustomValidators.notEqual(this.receivedGtin));
    return validators;
  }
}
